import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

const MOBILE_BULLETIN_SWIPE_DISMISS_PX = 78;
const MOBILE_BULLETIN_SWIPE_FAST_DISMISS_PX = 34;
const MOBILE_BULLETIN_SWIPE_FAST_DISMISS_VELOCITY = 0.5;
const MOBILE_BULLETIN_CLOSE_MS = 180;

export function useBulletinSurface({
  open,
  isMobile,
  onClose,
  popupPosition,
}: {
  open: boolean;
  isMobile: boolean;
  onClose: () => void;
  popupPosition: { top: number; right: number } | null;
}) {
  const [manualPosition, setManualPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [desktopDrag, setDesktopDrag] = useState<{
    x: number;
    y: number;
    top: number;
    right: number;
  } | null>(null);
  const [mobileOffset, setMobileOffset] = useState(0);
  const [isMobileDragging, setIsMobileDragging] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const pointerStartYRef = useRef(0);
  const pointerStartTimeRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const requestClose = useCallback(() => {
    if (!isMobile) {
      onClose();
      return;
    }
    if (isDismissing) return;
    setIsDismissing(true);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(onClose, MOBILE_BULLETIN_CLOSE_MS);
  }, [isDismissing, isMobile, onClose]);

  useEffect(() => {
    if (open) return;
    setManualPosition(null);
    setDesktopDrag(null);
    setMobileOffset(0);
    setIsMobileDragging(false);
    setIsDismissing(false);
    pointerIdRef.current = null;
    hasDraggedRef.current = false;
  }, [open]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!open || !isMobile) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isMobile, open, requestClose]);

  function handleMobilePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!isMobile || isDismissing) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest("button, input, textarea, select, [role='button']"))
      return;
    const scrollContainer = target.closest(
      "[data-bulletin-scroll]",
    ) as HTMLElement | null;
    if (scrollContainer && scrollContainer.scrollTop > 0) return;
    pointerIdRef.current = event.pointerId;
    pointerStartYRef.current = event.clientY;
    pointerStartTimeRef.current = performance.now();
    hasDraggedRef.current = false;
    setIsMobileDragging(false);
    setMobileOffset(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handleMobilePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (pointerIdRef.current !== event.pointerId || !isMobile || isDismissing)
      return;
    const deltaY = event.clientY - pointerStartYRef.current;
    if (deltaY <= 0) {
      if (hasDraggedRef.current) setMobileOffset(0);
      return;
    }
    if (deltaY < 6 && !hasDraggedRef.current) return;
    event.preventDefault();
    hasDraggedRef.current = true;
    setIsMobileDragging(true);
    setMobileOffset(Math.min(deltaY, 220));
  }

  function finishMobilePointerDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (pointerIdRef.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    const elapsed = Math.max(
      performance.now() - pointerStartTimeRef.current,
      1,
    );
    const velocity = mobileOffset / elapsed;
    const shouldDismiss =
      mobileOffset >= MOBILE_BULLETIN_SWIPE_DISMISS_PX ||
      (mobileOffset >= MOBILE_BULLETIN_SWIPE_FAST_DISMISS_PX &&
        velocity >= MOBILE_BULLETIN_SWIPE_FAST_DISMISS_VELOCITY);
    pointerIdRef.current = null;
    hasDraggedRef.current = false;
    if (shouldDismiss) {
      setMobileOffset(Math.max(mobileOffset, 140));
      requestClose();
      return;
    }
    setMobileOffset(0);
    setIsMobileDragging(false);
  }

  const panelPosition = (isMobile ? null : manualPosition) ?? popupPosition;
  const rootStyle = isMobile
    ? ({
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        pointerEvents: "none",
      } as const)
    : ({
        position: "fixed",
        top: panelPosition?.top,
        right: panelPosition?.right,
        zIndex: 9998,
      } as const);
  const mobileStyle = isMobile
    ? ({
        "--sticky-bulletin-drawer-drag-y": `${mobileOffset}px`,
      } as CSSProperties)
    : undefined;

  function beginDesktopDrag(event: React.MouseEvent) {
    if (isMobile || !panelPosition) return;
    setDesktopDrag({
      x: event.clientX,
      y: event.clientY,
      top: panelPosition.top,
      right: panelPosition.right,
    });
  }

  function moveDesktopDrag(event: React.MouseEvent) {
    if (isMobile || !desktopDrag) return;
    setManualPosition({
      top: Math.max(12, desktopDrag.top + event.clientY - desktopDrag.y),
      right: Math.max(12, desktopDrag.right - (event.clientX - desktopDrag.x)),
    });
  }

  return {
    panelPosition,
    rootStyle,
    mobileStyle,
    isMobileDragging,
    isDismissing,
    requestClose,
    handleMobilePointerDown,
    handleMobilePointerMove,
    finishMobilePointerDrag,
    beginDesktopDrag,
    moveDesktopDrag,
    finishDesktopDrag: () => setDesktopDrag(null),
  };
}
